// src/pages/EditFieldResearch.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface FormData {
  frostMitigation: string;
  noiseOrdinances: string;
  fuelTypes: string;
  cropInsurance: string;
  governmentSubsidies: string;
  existingCompetitors: string;
  marketShare: string;
  reasonsForChoosing: string;
  reasonsAgainstChoosing: string;
  competitorFeedbackPositive: string;
  competitorFeedbackNegative: string;
  sources: string;
}

const EditFieldResearch: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    frostMitigation: "",
    noiseOrdinances: "",
    fuelTypes: "",
    cropInsurance: "",
    governmentSubsidies: "",
    existingCompetitors: "",
    marketShare: "",
    reasonsForChoosing: "",
    reasonsAgainstChoosing: "",
    competitorFeedbackPositive: "",
    competitorFeedbackNegative: "",
    sources: ""
  });

  useEffect(() => {
    fetch(`/api/countries/${id}/field-research`)
      .then(res => res.json())
      .then(data =>
        setFormData({
          frostMitigation:            data.frost_mitigation            || "",
          noiseOrdinances:            data.noise_ordinances            || "",
          fuelTypes:                  data.fuel_types                  || "",
          cropInsurance:              data.crop_insurance              || "",
          governmentSubsidies:        data.government_subsidies || "",
          existingCompetitors:        data.existing_competitors        || "",
          marketShare:                data.market_share != null
                                        ? String(data.market_share)
                                        : "",
          reasonsForChoosing:         data.reasons_for_choosing        || "",
          reasonsAgainstChoosing:     data.reasons_against_choosing    || "",
          competitorFeedbackPositive: data.competitor_feedback_positive || "",
          competitorFeedbackNegative: data.competitor_feedback_negative || "",
          sources:                    data.sources                     || ""
        })
      )
      .catch(err => console.error("Error fetching field research", err));
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      frostMitigation:            formData.frostMitigation        || null,
      noiseOrdinances:            formData.noiseOrdinances        || null,
      fuelTypes:                  formData.fuelTypes              || null,
      cropInsurance:              formData.cropInsurance          || null,
      governmentSubsidies:        formData.governmentSubsidies    || null,
      existingCompetitors:        formData.existingCompetitors    || null,
      marketShare:                formData.marketShare === ""
                                    ? null
                                    : parseFloat(formData.marketShare),
      reasonsForChoosing:         formData.reasonsForChoosing      || null,
      reasonsAgainstChoosing:     formData.reasonsAgainstChoosing  || null,
      competitorFeedbackPositive: formData.competitorFeedbackPositive || null,
      competitorFeedbackNegative: formData.competitorFeedbackNegative || null,
      sources:                    formData.sources                       || null
    };

    fetch(`/api/countries/${id}/field-research`, {
      method:  "PUT",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(payload),
    })
      .then(() => navigate(`/country/${id}`))
      .catch(err => console.error("Error updating field research", err));
  };

  // Styles
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties      = { backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: "800px", margin: "30px auto", padding: "30px" };
  const headerStyle: React.CSSProperties    = { textAlign: "center", color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: "10px", marginBottom: "20px", fontSize: "24px" };
  const labelStyle: React.CSSProperties     = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties     = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties    = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Field Research</h1>
        <form onSubmit={handleSubmit}>
          <h3 style={{ color: "#e41e26", borderBottom: "1px solid #e41e26", paddingBottom: "5px", marginTop: "15px" }}>
            Current Practices
          </h3>
          <label style={labelStyle}>Frost Mitigation:</label>
          <input type="text" name="frostMitigation" style={inputStyle} value={formData.frostMitigation} onChange={handleChange} placeholder="List current practices" />
          <label style={labelStyle}>Noise Ordinances:</label>
          <input type="text" name="noiseOrdinances" style={inputStyle} value={formData.noiseOrdinances} onChange={handleChange} placeholder="Explain if necessary" />
          <label style={labelStyle}>Fuel Types:</label>
          <input type="text" name="fuelTypes" style={inputStyle} value={formData.fuelTypes} onChange={handleChange} placeholder="List available and preferred types" />
          <label style={labelStyle}>Crop Insurance:</label>
          <input type="text" name="cropInsurance" style={inputStyle} value={formData.cropInsurance} onChange={handleChange} placeholder="Provide details" />
          <label style={labelStyle}>Government Subsidies:</label>
          <input type="text" name="governmentSubsidies" style={inputStyle} value={formData.governmentSubsidies} onChange={handleChange} placeholder="Provide details" />

          <h3 style={{ color: "#e41e26", borderBottom: "1px solid #e41e26", paddingBottom: "5px", marginTop: "15px" }}>
            Market Presence
          </h3>
          <label style={labelStyle}>Existing Competitors:</label>
          <input type="text" name="existingCompetitors" style={inputStyle} value={formData.existingCompetitors} onChange={handleChange} placeholder="List competitors" />
          <label style={labelStyle}>Market Share:</label>
          <input type="text" name="marketShare" style={inputStyle} value={formData.marketShare} onChange={handleChange} placeholder="Enter percentages" />

          <h3 style={{ color: "#e41e26", borderBottom: "1px solid #e41e26", paddingBottom: "5px", marginTop: "15px" }}>
            Customer Insights
          </h3>
          <label style={labelStyle}>Reasons for Choosing Our Products:</label>
          <textarea name="reasonsForChoosing" style={inputStyle} value={formData.reasonsForChoosing} onChange={handleChange} placeholder="Summarize feedback and include quotes where applicable" />
          <label style={labelStyle}>Reasons Against Choosing Our Products:</label>
          <textarea name="reasonsAgainstChoosing" style={inputStyle} value={formData.reasonsAgainstChoosing} onChange={handleChange} placeholder="Summarize feedback" />
          <label style={labelStyle}>Feedback on Competitors - Positive Aspects:</label>
          <textarea name="competitorFeedbackPositive" style={inputStyle} value={formData.competitorFeedbackPositive} onChange={handleChange} placeholder="Summarize" />
          <label style={labelStyle}>Feedback on Competitors - Negative Aspects:</label>
          <textarea name="competitorFeedbackNegative" style={inputStyle} value={formData.competitorFeedbackNegative} onChange={handleChange} placeholder="Summarize" />

          <h3 style={{ color: "#e41e26", borderBottom: "1px solid #e41e26", paddingBottom: "5px", marginTop: "15px" }}>
            Sources
          </h3>
          <label style={labelStyle}>Sources:</label>
          <input type="text" name="sources" style={inputStyle} value={formData.sources} onChange={handleChange} placeholder="List all information sources" />

          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/country/${id}`)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFieldResearch;